import { Helmet } from "react-helmet-async";
import { SITE_NAME, SITE_URL, absoluteUrl } from "@/lib/siteConfig";
import { useI18n } from "@/context/I18nContext";
import { SUPPORTED_LANGS, OG_LOCALE, withLang } from "@/lib/i18nRoutes";

const SITE = SITE_NAME;

export const Seo = ({ title, description, path = "/", image, jsonLd, type = "website", schemaOnly = false }) => {
  const { lang } = useI18n();

  // schemaOnly: emit ONLY structured data (used when a page needs a second
  // JSON-LD block) so we never render a duplicate <title> / meta tags.
  if (schemaOnly) {
    return (
      <Helmet>
        {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      </Helmet>
    );
  }

  const fullTitle = title ? `${title} | ${SITE}` : SITE;
  // `path` is the BARE (non-prefixed) path; canonical is the current language URL.
  const canonical = `${SITE_URL}${withLang(path, lang)}`;
  // Open Graph requires absolute image URLs.
  const ogImage = image
    ? (image.startsWith("http") ? image : absoluteUrl(image))
    : undefined;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates for every language + x-default (English root) */}
      {SUPPORTED_LANGS.map((code) => (
        <link key={code} rel="alternate" hrefLang={code} href={`${SITE_URL}${withLang(path, code)}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${withLang(path, "en")}`} />

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={OG_LOCALE[lang] || "en_IE"} />
      {SUPPORTED_LANGS.filter((c) => c !== lang).map((code) => (
        <meta key={`ogl-${code}`} property="og:locale:alternate" content={OG_LOCALE[code]} />
      ))}
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};
